import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { ArrowDown, Globe, Languages, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const HeroSection = ({ productCount = 20 }) => {
  const { t } = useLanguage();
  const [homepageContent, setHomepageContent] = useState(null);

  useEffect(() => {
    loadHomepageContent();
  }, []);

  const loadHomepageContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/site-content/homepage`);
      if (response.ok) {
        const data = await response.json();
        if (data.content) {
          setHomepageContent(data.content);
        }
      }
    } catch (err) {
      console.error('Error loading homepage content:', err);
    }
  };

  const heroTitle = homepageContent?.hero?.title || t('hero.title');
  const heroSubtitle = homepageContent?.hero?.subtitle || t('hero.subtitle');
  const ctaText = homepageContent?.hero?.ctaText || t('hero.cta');

  const stats = [
    {
      icon: Package,
      value: homepageContent?.stats?.stat1Value || '+30.000',
      label: homepageContent?.stats?.stat1Label || t('hero.stats.products'),
    },
    {
      icon: Globe,
      value: homepageContent?.stats?.stat2Value || '+16',
      label: homepageContent?.stats?.stat2Label || t('hero.stats.countries'),
    },
    {
      icon: Languages,
      value: homepageContent?.stats?.stat3Value || '+10',
      label: homepageContent?.stats?.stat3Label || t('hero.stats.filters'),
    },
  ];

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-hero-gradient" />
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="text-center lg:text-left">
            <img 
              src="/logo_purolink_full.webp" 
              alt="Purolink Logo" 
              className="h-16 sm:h-20 w-auto mb-6 mx-auto lg:mx-0 animate-fade-in-up"
            />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight mb-6 animate-fade-in-up">
              {heroTitle}
            </h1>
            <p className="text-lg sm:text-xl text-primary-foreground/80 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              {heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <Button
                asChild
                size="lg"
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold px-8 shadow-lg"
              >
                <a href="#catalog">
                  {ctaText}
                  <ArrowDown className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-12 pt-8 border-t border-primary-foreground/20 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              {stats.map((stat, index) => (
                <div key={index} className="text-center lg:text-left">
                  <div className="flex items-center justify-center lg:justify-start gap-2 mb-1">
                    <stat.icon className="h-5 w-5 text-primary-foreground/70" />
                    <span className="text-2xl sm:text-3xl font-bold text-primary-foreground">
                      {stat.value}
                    </span>
                  </div>
                  <span className="text-sm text-primary-foreground/70">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden lg:block animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop&q=80"
                alt="SEO Analytics Dashboard"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/40 to-transparent" />
            </div>
            
            <div className="absolute -bottom-6 -left-6 bg-card rounded-xl shadow-xl p-4 animate-bounce-soft">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <span className="text-success font-bold">DR</span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Average</p>
                  <p className="font-bold text-foreground">72+</p>
                </div>
              </div>
            </div>

            <div className="absolute -top-4 -right-4 bg-card rounded-xl shadow-xl p-4 animate-bounce-soft" style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <span className="text-accent font-bold">€</span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">From</p>
                  <p className="font-bold text-foreground">€45</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
        >
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z"
            className="fill-background"
          />
        </svg>
      </div>
    </section>
  );
};
